/**
 * SALUD — la vista de DETALLE.  (US3, FR-029, FR-030a)
 *
 * La portada lleva el resumen (`SaludInfra`): cuando todo va bien, cuatro
 * líneas. Esta página es para cuando hay que mirar de verdad — y lo que aporta
 * es justo lo que la portada esconde a propósito: **los contenedores, uno a uno**.
 *
 * *"¿Está en marcha la agenda de la clínica tal?"* es una pregunta legítima que
 * una lista de problemas no puede responder: si un contenedor no aparece, no se
 * sabe si es que está bien o es que no existe.
 *
 * Solo `admin`: `/overview/health` responde **404** a cualquier otro ámbito.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'

import { Disponibilidad } from '../components/Disponibilidad'
import type { Salud } from '../components/SaludInfra'
import { Badge, type PropsBadge } from '../components/ui/badge'
import { api } from '../lib/api'

type Tono = NonNullable<PropsBadge['tone']>

const TONO_BOT: Record<Salud['bot']['estado'], Tono> = {
    healthy: 'success',
    degraded: 'warning',
    down: 'danger',
}
const TONO_CW: Record<Salud['chatwoot']['estado'], Tono> = {
    up: 'success',
    degraded: 'warning',
    down: 'danger',
}
const TONO_AGENDA: Record<Salud['agendas'][number]['estado'], Tono> = {
    up: 'success',
    down: 'danger',
    'sin-configurar': 'warning',
}

function gb(bytes: number): string {
    return `${(bytes / 1e9).toFixed(1)} GB`
}

/** Tres categorías, no dos: "parado" no es "roto". Ver el backend. */
function categoria(c: Salud['contenedores']['lista'][number]): 'ok' | 'parado' | 'problema' {
    if (c.estado === 'running') return c.salud === 'unhealthy' ? 'problema' : 'ok'
    if (c.estado === 'restarting' || c.estado === 'dead') return 'problema'
    return 'parado'
}

const PUNTO = { ok: 'bg-emerald-500', parado: 'bg-neutral-600', problema: 'bg-rose-500' } as const
const TEXTO = { ok: 'text-neutral-400', parado: 'text-neutral-600', problema: 'text-rose-300' } as const

/** Lo que le pasó, en cristiano. `Exited (0)` y `Exited (137)` no son lo mismo. */
function motivo(c: Salud['contenedores']['lista'][number]): string {
    if (c.salud === 'unhealthy') return 'fallando su chequeo'
    if (c.estado === 'restarting') return 'reiniciándose en bucle'
    if (c.estado === 'exited') return c.salida === 0 ? 'parado' : `salió con código ${c.salida ?? '?'}`
    return c.estado
}

export function Health() {
    const { data, isPending, isError, refetch, isFetching } = useQuery({
        queryKey: ['salud-infra'], // MISMA clave que la portada: una sola verdad
        queryFn: () => api.get<Salud>('/overview/health'),
    })

    if (isPending) return <p className="text-sm text-neutral-500">Comprobando salud…</p>
    if (isError || !data) return <p className="text-sm text-red-400">No se pudo comprobar la salud.</p>

    const { servidor, contenedores, chatwoot, bot, agendas } = data
    const m = servidor.metricas

    // `agenda:2` → "Agenda · Clínica…". El nombre ya lo tenemos aquí; pasarlo
    // evita que el histórico lo pida por segunda vez o se lo invente.
    const nombresAgenda = new Map(agendas.map((a) => [`agenda:${String(a.accountId)}`, `Agenda · ${a.name}`]))

    return (
        <div className="max-w-3xl space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <Link to="/" className="inline-flex min-h-11 items-center text-xs text-neutral-500 hover:text-neutral-300 md:min-h-0">
                    ← Toda la operación
                </Link>
                <h1 className="text-sm font-semibold text-neutral-100">Salud del sistema</h1>
                <button
                    onClick={() => void refetch()}
                    disabled={isFetching}
                    className="inline-flex min-h-11 items-center text-xs text-neutral-500 hover:text-neutral-300 disabled:opacity-50 md:min-h-0"
                >
                    {isFetching ? 'Actualizando…' : 'Actualizar'}
                </button>
            </div>

            <Disponibilidad agendas={nombresAgenda} />

            <Tarjeta titulo="Servidor">
                <div className="mb-2 flex items-center gap-2">
                    <Badge tone={servidor.estado === 'up' ? 'success' : 'warning'}>{servidor.estado}</Badge>
                    <span className="text-xs text-neutral-500">{servidor.detalle}</span>
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
                    <Dato
                        k="CPU"
                        // `null` = aún no hay dos muestras. No se inventa un 0%.
                        v={m.cpuPct === null ? 'midiendo…' : `${m.cpuPct}%`}
                        extra={
                            m.cpuVentanaSeg === null
                                ? `${m.nucleos} núcleos · carga ${m.carga1}`
                                : `carga ${m.carga1} · media de ${
                                      m.cpuVentanaSeg < 90
                                          ? `${m.cpuVentanaSeg} s`
                                          : `${Math.round(m.cpuVentanaSeg / 60)} min`
                                  }`
                        }
                    />
                    <Dato
                        k="Memoria"
                        v={`${m.memoria.usoPct}%`}
                        extra={`${gb(m.memoria.disponibleBytes)} libres de ${gb(m.memoria.totalBytes)}`}
                    />
                    <Dato
                        k="Disco"
                        v={m.disco ? `${m.disco.usoPct}%` : 'sin lectura'}
                        extra={m.disco ? `${gb(m.disco.disponibleBytes)} libres de ${gb(m.disco.totalBytes)}` : '—'}
                    />
                </dl>
            </Tarjeta>

            <Tarjeta titulo="Bot (Agendify)">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={TONO_BOT[bot.estado]}>{bot.estado}</Badge>
                    <span className="text-sm text-neutral-400">{bot.detalle}</span>
                </div>
            </Tarjeta>

            <Tarjeta titulo="Chatwoot">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={TONO_CW[chatwoot.estado]}>{chatwoot.estado}</Badge>
                    <span className="text-sm text-neutral-400">{chatwoot.detalle}</span>
                    {chatwoot.version && <span className="ml-auto text-xs text-neutral-600">v{chatwoot.version}</span>}
                </div>
            </Tarjeta>

            {/* Cada agenda por separado. EasyAppointments NO es multi-tenant: hay
                una instancia por negocio. Un semáforo único escondería una
                clínica que lleva horas sin poder dar citas. */}
            <Tarjeta titulo="Agendas (una por negocio)">
                {agendas.length === 0 ? (
                    <p className="text-sm text-neutral-600">Ningún negocio configurado.</p>
                ) : (
                    <ul className="space-y-2">
                        {agendas.map((a) => (
                            <li key={a.accountId} className="flex flex-wrap items-center gap-2 text-sm">
                                <Badge tone={TONO_AGENDA[a.estado]}>{a.estado}</Badge>
                                <span className="text-neutral-300">{a.name}</span>
                                <span className="ml-auto text-xs text-neutral-600">{a.detalle}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Tarjeta>

            <Tarjeta
                titulo={
                    contenedores.total === null
                        ? 'Contenedores'
                        : `Contenedores · ${contenedores.sanos} en marcha` +
                          (contenedores.parados ? `, ${contenedores.parados} parados` : '') +
                          (contenedores.problemas ? `, ${contenedores.problemas} fallando` : '')
                }
            >
                {contenedores.total === null ? (
                    // ⚠️ Ni verde ni "0 caídos": hemos perdido la capacidad de mirar.
                    <p className="text-sm text-amber-500">{contenedores.detalle}</p>
                ) : (
                    <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                        {ordenar(contenedores.lista).map((c) => (
                            <li key={c.nombre} className="flex items-center gap-2 text-xs">
                                <span
                                    className={`inline-block size-1.5 shrink-0 rounded-full ${PUNTO[categoria(c)]}`}
                                />
                                <span className={TEXTO[categoria(c)]}>{c.nombre}</span>
                                <span className="ml-auto text-[11px] text-neutral-600">{motivo(c)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Tarjeta>

            <p className="text-[11px] text-neutral-600">
                Medido a las {new Date(data.medidoEn).toLocaleTimeString('es-ES')}
            </p>
        </div>
    )
}

/** Lo que falla primero; lo parado al final. La lista no debe hacer buscar. */
const PESO = { problema: 0, ok: 1, parado: 2 } as const
function ordenar(lista: Salud['contenedores']['lista']): Salud['contenedores']['lista'] {
    return [...lista].sort((a, b) => PESO[categoria(a)] - PESO[categoria(b)])
}

function Dato({ k, v, extra }: { k: string; v: string; extra: string }) {
    return (
        <div>
            <dt className="text-neutral-500">{k}</dt>
            <dd className="tabular-nums text-neutral-200">{v}</dd>
            <dd className="text-[11px] text-neutral-600">{extra}</dd>
        </div>
    )
}

function Tarjeta({ titulo, children }: { titulo: string; children: React.ReactNode }) {
    return (
        <section className="rounded-lg border border-neutral-800 p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">{titulo}</h2>
            {children}
        </section>
    )
}
